import React, { useContext, useState } from 'react'
import { FormRow, useForm } from '@cicero/ui/forms'
import {
  Button, Checkbox, ColorChooser, Dialog, DialogActions, EasyTable,
  Form, FormControlLabel, FormGroup, FormSection, TextField, useObservable,
} from '@cicero/ui'
import { AttributeDoc, AttributeOptionSchema, AttributeSchema, AttributeType, StoreContext, useModel } from 'models'
import { AttributeOption } from '../../forms/AttributeOptionChooser'
import { AttributeTypeSelect, PageFilters } from 'forms'

export const AttributeSettings = () => {
  const Attributes = useModel(AttributeSchema)
  const attributes = useObservable(() => Attributes.QUERY$(), [], [])
  const [editing, setEditing] = useState<AttributeDoc|null>(null)

  const addNewAttribute = async () => {
    const attr = await Attributes.NEW({})
    setEditing(attr)
  }

  return <FormSection
    title="Attributes"
    action={<Button variant="contained" color="primary" onClick={addNewAttribute}>New Attribute</Button>}
  >
    <EasyTable
      data={attributes}
      columns={[
        { title: 'Attribute Name', value: (d) => d.label },
        { title: 'Key', value: (d) => d.key },
        { title: 'Type', value: (d) => AttributeSchema.TYPE_CONFIG[d.type].label },
        { title: '', value: (d) => <Button onClick={() => setEditing(d)}>Edit</Button>, cellProps: { align: 'right' } },
      ]}
    />
    <Dialog fullWidth open={editing !== null} onClose={() => setEditing(null)}>
      { editing ? <AttributeForm attribute={editing} onClose={() => setEditing(null)} /> : null }
    </Dialog>
  </FormSection>
}

export const AttributeForm = ({ attribute, onClose }: { attribute: AttributeDoc, onClose: () => void }) => {
  const store = useContext(StoreContext)
  const $form = useForm({
    async onSubmit({ values }) {
      const { label, key, type } = values
      await attribute.atomicUpdate(a => {
        // Creating a new one
        if (!a.id) {
          const { session } = store
          a.type = parseInt(type)
          a.owner_id = session?.url.path || ''
        }

        a.label = label
        a.key = key
        return a
      })

      onClose()
    },
    initialValues: () => ({
      label: attribute.label,
      key: attribute.key,
      type: String(attribute.type || AttributeType.text),
      min: attribute.min,
      max: attribute.max,
      filters: [],
      hasDefault: false,
      defaultValue: [] as any[],
    }),
  })

  return <Form onSubmit={$form.handleSubmit} noValidate>
    <h1>Edit Attribute</h1>

    { attribute.id ? null : <AttributeTypeSelect label="Attribute Type" {...$form.props('type')} /> }

    <FormRow>
      <TextField label="Label" required variant="outlined" {...$form.props('label')} />
      <TextField label="Key" required variant="outlined" {...$form.props('key')} />
    </FormRow>

    {
      [AttributeType.text, AttributeType.number].includes(attribute.type)
        ? <FormRow>
          <TextField label="Min Length" type="number" variant="outlined" {...$form.props('min')} />
          <TextField label="Max Length" type="number" variant="outlined" {...$form.props('max')} />
        </FormRow> : null
    }
    {
      [AttributeType.option].includes(attribute.type)
        ? <FormRow>
          <span /><span /><span /><span />
          <TextField label="Max Selections" type="number" variant="outlined" {...$form.props('max')} />
        </FormRow> : null
    }
    {
      attribute.type === AttributeType.option ? <AttributeOptionForm attribute={attribute} /> : null
    }
    {
      attribute.type === AttributeType.linkedpage
        ? <FormGroup title="Restrict Pages">
          <PageFilters {...$form.props('filters')} />
        </FormGroup> : null
    }

    <FormRow>
      <FormControlLabel
        control={<Checkbox checked={$form.values.hasDefault} onChange={(e) => $form.setPartialValues({ hasDefault: e.target.checked })} name="checkedA" />}
        label="Default Value"
      />
      {/* <AttributeValueEditor
        attribute={attribute}
        values={[]}
        page={{} as any}
        onSubmit={(v) => $form.setPartialValues({ defaultValue: v })}
        onCancel={() => {}}
        shouldSubmit
      /> */}
    </FormRow>

    {/* Text: Min/Max Length, Default */}
    {/* Formula:  */}
    {/* Option: Max, List of Options, Treat as Inbox status */}
    {/* Linked Page: Max, Filter */}
    {/* Linked User: Max, Treat as assignment, Restrict groups */}
    {/* Number: Min/Max, Default */}
    {/* Date: Min/Max, Default, Treat as due date */}
    {/* Boolean: Label, Default */}
    {/* <pre>{JSON.stringify($form.values, null, 2)}</pre> */}

    <DialogActions>
      <Button color="primary" onClick={() => onClose()}>Cancel</Button>
      <Button type="submit" color="primary">Save</Button>
    </DialogActions>
  </Form>
}

export const AttributeOptionForm = ({ attribute }: { attribute: AttributeDoc }) => {
  const AttributeOptions = useModel(AttributeOptionSchema)
  const options = useObservable(() => AttributeOptions.QUERY$({ selector: { attribute_id: attribute.id } }), [], [attribute])

  return  <FormGroup title="Manage Options">
    <EasyTable
      data={options}
      columns={[
        { title: 'Label', value: (o) => <AttributeOption option={o} /> },
        { title: 'Color', value: (o) => <ColorChooser value={o.color} />, cellProps: { align: 'right' } },
      ]}
    />
  </FormGroup>
}
